<template>
  <div>
    <!-- Toastification loading content export -->
    <toastification-loading-content ref="loadingToast" />

    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <b-card class="mx-2" no-body>
      <b-row>
        <b-col cols="12" md="6" class="mx-2 mt-2">
          <p class="mb-0">
            Las cifras registradas para la depreciación contemplan meses de 30 días.
          </p>
          <p class="mb-0">Las cifras se actualizan a día vencido.</p>
        </b-col>
      </b-row>

      <b-row class="m-2">
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label class="text-capitalize">{{ $t("table.show") }}</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block mx-50"
          />
          <label class="text-capitalize">{{ $t("table.entries") }}</label>
        </b-col>
        <b-col cols="12" md="6">
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              :placeholder="$t('table.search') + '...'"
            />
            <b-button
              v-if="$can('create', 'Inventory')"
              variant="secondary"
              @click="openAdd()"
            >
              <span class="text-nowrap text-capitalize">{{
                $t("actions.add") + " " + $tc("inventory.title", 1)
              }}</span>
            </b-button>
            <b-button
              v-if="$can('import', 'Inventory')"
              v-b-modal.import_file
              variant="info"
              class="text-capitalize ml-1"
              type="button"
              :disabled="buttonExport"
            >
              <feather-icon icon="UploadCloudIcon" size="15" />
            </b-button>
            <b-button
              v-if="$can('export', 'Inventory')"
              @click="exportExcel()"
              variant="success"
              class="text-capitalize ml-1"
              type="button"
              :disabled="buttonExport"
            >
              <feather-icon icon="DownloadCloudIcon" size="15" />
            </b-button>
          </div>
        </b-col>
      </b-row>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        :empty-text="$t('no_record_found')"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #cell(status)="data">
          <span>{{ getStatusLabel(data.item.status) }}</span>
        </template>

        <template #cell(equipment_cost)="data">
          <span>{{ getCurrencyAmount(data.item.equipment_cost) }}</span>
        </template>

        <template #cell(accumulated_depreciation)="data">
          <span>{{ getCurrencyAmount(data.item.accumulated_depreciation) }}</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              @click="openEdit(data.item)"
              v-if="$can('update', 'Inventory')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t("actions.edit") }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="confirmDelete(data.item.id)"
              v-if="$can('delete', 'Inventory')"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50 text-capitalize"
                >{{ $t("actions.delete") }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">
              {{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Modal Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :category-options="categoryOptions"
          :category-filter.sync="filters.categories"
          :region-options="regionOptions"
          :region-filter.sync="filters.regions"
          :unit-options="unitOptions"
          :unit-filter.sync="filters.units"
          :unit-name-filter.sync="filters.unitName"
          :equipment-type-filter.sync="filters.equipmentTypes"
          :brand-filter.sync="filters.brands"
          :model-filter.sync="filters.model"
          :useful-life-months-filter.sync="filters.usefulLifeMonths"
          :remaining-life-months-filter.sync="filters.remainingLifeMonths"
          :status-filter.sync="filters.status"
          :project-id-filter.sync="filters.projectIds"
          :project-name-filter.sync="filters.projectName"
          :installation-date-filter.sync="filters.installationDate"
          :depreciation-start-date-filter.sync="filters.depreciationStartDate"
          :user-options="userOptions"
          :country-options="countryOptions"
          :municipality-options="municipalityOptions"
          :status-options="statusOptions"
          :user-filter.sync="filters.users"
          :responsable-filter.sync="filters.responsable"
          :assigned-filter.sync="filters.assigned"
          :start-date.sync="filters.startDate"
          :end-date.sync="filters.endDate"
          :country-filter.sync="filters.countries"
          :municipality-filter.sync="filters.municipalities"
          :rems-filter.sync="filters.rems"
          :sirh-filter.sync="filters.sirh"
          :brand-options="brandOptions"
          :project-options="projectOptions"
          :equipment-options="equipmentOptions"
        />
      </template>
    </filter-modal>

    <!-- Modal Import -->
    <div>
      <b-modal
        id="import_file"
        :title="`${$t('messages.import.title')}`"
        no-close-on-esc
        no-close-on-backdrop
        ref="modalImport"
      >
        <vue-dropzone
          id="dropzone_file"
          ref="dropzoneFile"
          :options="dropzoneOptions"
          class="dropzone-file"
          @vdropzone-file-added="addFile"
          @vdropzone-removed-file="removeFile"
        />
        <div v-if="importMessage.length > 0" class="mt-2 scroll">
          <p class="mb-1">
            {{ $t("inventory.error_title") }}
          </p>
          <span v-for="(message, index) in importMessage" :key="index">
            <p class="text-danger mb-0">
              {{ message }}
            </p>
          </span>
        </div>
        <template #modal-footer>
          <a :href="import_example_url" download>{{
            $t("messages.import.example_file")
          }}</a>
          <b-button
            variant="secondary"
            @click="closeImportModal()"
            :disabled="buttonExport"
          >
            {{ $t("actions.cancel") }}
          </b-button>
          <b-button variant="primary" @click="importExcel" :disabled="buttonExport">
            {{ $t("actions.accept") }}
          </b-button>
        </template>
      </b-modal>
    </div>

    <!-- Modal Form -->
    <inventory-form-modal
      :item-edit="itemEdit"
      @form-saved="formSaved()"
      :brand-options="brandOptions"
      :project-options="projectOptions"
      :equipment-options="equipmentOptions"
      :units-options="unitsOptions"
      :status-options="statusOptions"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BDropdown,
  BDropdownItem,
  BTable,
} from "bootstrap-vue";
import vSelect from "vue-select";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import { ref, getCurrentInstance, onMounted, watch } from "@vue/composition-api";

import Pagination from "@/components/Pagination.vue";

import useList from "./useList";
import InventoryFormModal from "./InventoryFormModal.vue";
import Filters from "./Filters.vue";
import store from "@/store";
import Swal from "sweetalert2";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationRemsLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";

import inventoryStatus from "./constants/inventoryStatus";

const formatter = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
});

export default {
  name: "IndexInventories",
  components: {
    BreadcrumbMenu,
    BRow,
    BCol,
    BTable,
    vSelect,
    InventoryFormModal,
    Pagination,
    ButtonFilters,
    FilterModal,
    Filters,
    vueDropzone: vue2Dropzone,
    ToastificationContent,
    ToastificationLoadingContent,
    ToastificationRemsLoadingContent,
  },
  setup(_, context) {
    const vm = getCurrentInstance().proxy;

    const toast = useToast();

    const itemEdit = ref(null);

    const {
      tableColumns,
      searchQuery,
      dataMeta,
      perPage,
      currentPage,
      totalList,
      perPageOptions,
      fetchList,
      refetchData,
      refListTable,
      clearSearchFilters,
      filtersCount,
      filters,
      buildFilters,
      unitOptions,
      userOptions,
      countryOptions,
      municipalityOptions,
      regionOptions,
      categoryOptions,
      exportExcel,
      buttonExport,
      brandOptions,
      projectOptions,
      equipmentOptions,
      unitsOptions,
      statusOptions,
    } = useList();

    function openAdd() {
      itemEdit.value = null;
      vm.$root.$emit("bv::show::modal", "inventory-form-modal");
    }

    function openEdit(item) {
      itemEdit.value = item;
      vm.$root.$emit("bv::show::modal", "inventory-form-modal");
    }

    function closeModal() {
      vm.$root.$emit("bv::hide::modal", "inventory-form-modal");
      setTimeout(() => {
        itemEdit.value = null;
      }, 500);
    }

    function closeImportModal() {
      importMessage.value = [];
      vm.$root.$emit("bv::hide::modal", "import_file");
    }

    function formSaved() {
      closeModal();
      refetchData();
    }

    const confirmDelete = (id) => {
      Swal.fire({
        title: context.parent.$i18n.t("messages.delete.title"),
        text: context.parent.$i18n.t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: context.parent.$i18n.t("actions.delete"),
        cancelButtonText: context.parent.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          axios.delete(`v1/inventories/${id}`).then((response) => {
            refetchData();
            toast({
              component: ToastificationContent,
              props: {
                title: response.data.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
          });
        }
      });
    };

    const update = (val) => {
      currentPage.value = val;
    };

    const modalImport = ref();
    const importMessage = ref([]);
    const loadingToast = ref(null);
    const dropzoneFile = ref(null);
    const excelFile = ref(null);

    const trans = {
      "messages.import.upload": vm.$t("messages.import.upload"),
      "messages.import.uploading_file": vm.$t("messages.import.uploading_file"),
      "messages.import.upload_succesfully": vm.$t("messages.import.upload_succesfully"),
      "messages.export.download": vm.$t("messages.export.download"),
      "messages.export.Generating_file": vm.$t("messages.export.Generating_file"),
      "messages.error.server_error": vm.$t("messages.error.server_error"),
      "messages.delete.title": vm.$t("messages.delete.title"),
      "messages.delete.body": vm.$t("messages.delete.body"),
      "actions.delete": vm.$t("actions.delete"),
      "actions.cancel": vm.$t("actions.cancel"),
    };

    const addFile = (file) => {
      excelFile.value = file;
      importMessage.value = [];
      dropzoneFile.value = file;
      if (file.size > 10000000) {
        dropzoneFile.value = null;
      }
    };

    const removeFile = () => {
      excelFile.value = null;
      dropzoneFile.value = null;
    };

    const importExcel = () => {
      if (dropzoneFile.value == null) return;
      if (excelFile.value == null) return;

      loadingToast.value.show(
        trans["messages.import.upload"],
        trans["messages.import.uploading_file"],
        "secondary"
      );

      buttonExport.value = true;
      importMessage.value = [];

      let formData = new FormData();
      formData.append("file", excelFile.value);
      formData.append("action", "import");

      axios
        .post("v1/inventories", formData)
        .then((response) => {
          if (response.data.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.data.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            excelFile.value = null;
            modalImport.value.hide();
          }
        })
        .catch((error) => {
          const message = error?.response?.data?.message
            ? error.response.data.message
            : "Something went wrong";

          const rowErrors = error?.response?.data?.data ? error.response.data.data : [];

          importMessage.value = rowErrors;
          loadingToast.value.close();
          toast({
            component: ToastificationContent,
            props: {
              title: `${message}`,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          buttonExport.value = false;
          loadingToast.value.close();

          refetchData();
        });
    };

    const getStatusLabel = (status) => {
      switch (status) {
        case inventoryStatus.INSTALLED:
          return vm.$t("inventory.status.installed");
        case inventoryStatus.NEW_CUSTODY:
          return vm.$t("inventory.status.new_custody");
        case inventoryStatus.PAUSED:
          return vm.$t("inventory.status.paused");
        default:
          return null;
      }
    };

    const getCurrencyAmount = (amount) => {
      if (!amount) return null;
      return formatter.format(amount);
    };

    return {
      searchQuery,
      currentPage,
      totalList,
      dataMeta,
      update,
      itemEdit,
      formSaved,
      refetchData,
      refListTable,
      perPage,
      perPageOptions,
      fetchList,
      openAdd,
      confirmDelete,
      openEdit,
      tableColumns,
      clearSearchFilters,
      filtersCount,
      filters,
      buildFilters,
      unitOptions,
      userOptions,
      countryOptions,
      municipalityOptions,
      statusOptions,
      regionOptions,
      categoryOptions,
      exportExcel,
      buttonExport,
      //importExcel,
      modalImport,
      loadingToast,
      removeFile,
      dropzoneOptions: {
        url: "https://httpbin.org/post",
        paramName: "file",
        autoProcessQueue: false,
        addRemoveLinks: true,
        dictRemoveFile: vm.$t("actions.delete"),
        acceptedFiles: ".xlsx,.xls",
        maxFilesize: 10,
        dictDefaultMessage: vm.$t("messages.import.select_file") + " (.xlsx .xls)",
      },
      dropzoneFile,
      importMessage,
      addFile,
      importExcel,
      import_example_url: `${window.__env.VUE_APP_BASE_URL}/import_examples/import_inventarios.xlsx`,
      brandOptions,
      projectOptions,
      equipmentOptions,
      unitsOptions,
      getStatusLabel,
      getCurrencyAmount,
      closeImportModal,
    };
  },
};
</script>

